<template>
    <div>
        <div class="stick-below-nav">
            <div class="block sm:hidden text-xl text-gray-800 p-2" @click="$emit('close', mutableMaintenanceRequest)">
                <i class="mdi mdi-arrow-left"></i>
            </div>

            <div class="bg-white h-full rounded-md shadow p-2 relative">
                <div class="flex flex-col my-1 px-2">
                    <div class="flex flex-wrap">
                        <div class="flex-1 leading-5 font-medium text-indigo-600 hover:text-indigo-800 transition duration-300">
                            <div v-if="userHasLinkMaintenanceRequestsPermission && mutableMaintenanceRequest && !mutableMaintenanceRequest.archived_at"
                                 class="block" :class="!assetLinkShow ? 'truncate' :''">
                                <button v-show="!assetLinkShow"
                                        @click.prevent="openAssetLink()"
                                        class="mb-1 text-base font-medium">
                                    <span v-if="mutableMaintenanceRequest.asset && mutableMaintenanceRequest.asset.name" class="flex items-center gap-2">
                                        {{ mutableMaintenanceRequest.asset.name }}
                                        <button @click.stop="linkOrUnlinkMaintenanceRequestToAsset(mutableMaintenanceRequest)">
                                            <icon-x-circle class="w-4 h-4 hover:text-indigo-800 transition duration-300"></icon-x-circle>
                                        </button>
                                    </span>
                                    <span v-else>Link Asset</span>
                                </button>

                                <!-- Asset -->
                                <div v-show="assetLinkShow">
                                    <div class="flex mr-2 my-2">
                                        <remote-select
                                            class="flex-1"
                                            v-model="selectedAsset"
                                            ref="selectAsset"
                                            @focus="searchAssets"
                                            @input="closeAssetLink()"
                                            :remote-method="searchAssets"
                                            @clicked-outside="handleAssetLinkingClickOutside"
                                            :loading="loading"
                                            :data="assets"
                                            item-label="name"
                                            placeholder="Start typing to search assets, addresses or linked contacts">
                                            <template slot-scope="props">
                                                <assets-drop-down-item :asset="props.item"></assets-drop-down-item>
                                            </template>
                                        </remote-select>
                                    </div>
                                    <div class="text-red-500 text-xs break-normal mt-1" v-show="assetLinkForm.errors.has('asset_id')">
                                        {{ assetLinkForm.errors.get("asset_id") }}
                                    </div>
                                </div>
                            </div>

                            <div v-else>
                                <span v-if="mutableMaintenanceRequest.asset && mutableMaintenanceRequest.asset.name">{{ mutableMaintenanceRequest.asset.name }}</span>
                            </div>

                            <div class="font-normal text-sm text-gray-500 whitespace-nowrap">
                                Reported
                                <time class="font-medium text-gray-600 " :datetime="mutableMaintenanceRequest.created_at">
                                    {{ mutableMaintenanceRequest.created_at | datetime }}
                                </time>
                            </div>

                            <div v-if="mutableMaintenanceRequest.archived_at" class="absolute top-2 right-2 px-2 py-0.5 font-medium text-white bg-yellow-500 rounded-md">
                                Archived
                            </div>
                        </div>

                        <div v-if="!mutableMaintenanceRequest.archived_at">
                            <button class="button button-white my-2 mr-1 text-gray-400 hover:text-indigo-600 hover:border-indigo-600 transition duration-300"
                                    @click="createEmailForContact">
                                <icon-email v-if="!loadingEmail" class="h-4 w-4"></icon-email>
                                <icon-refresh v-else class="w-h h-4 animate-spin"></icon-refresh>
                            </button>

                            <button class="button button-white my-2 text-gray-400 hover:text-indigo-600 hover:border-indigo-600 transition duration-300"
                                    @click="showSmsForm">
                                <icon-device-mobile class="h-4 w-4"></icon-device-mobile>
                            </button>
                        </div>
                    </div>

                    <div class="flex flex-col text-sm leading-5 text-gray-500">
                        <div class="flex items-center gap-2">
                            <icon-user class="h-4 w-4 text-gray-400"></icon-user>
                            <span>
                                {{ mutableMaintenanceRequest.contact.name }}
                            </span>
                        </div>

                        <div class="flex items-center gap-2">
                            <icon-email class="h-4 w-4 text-gray-400"></icon-email>
                            <span>
                                {{ mutableMaintenanceRequest.contact.email }}
                            </span>
                        </div>

                        <div class="flex items-center gap-2">
                            <icon-phone class="h-4 w-4 text-gray-400"></icon-phone>
                            <span>
                                {{ mutableMaintenanceRequest.contact.mobile }}
                            </span>
                        </div>

                        <div class="flex">
                            Reference: {{ mutableMaintenanceRequest.property_reference }}
                        </div>

                        <div class="flex my-4">
                            {{ mutableMaintenanceRequest.reporter_comments }}
                        </div>
                    </div>
                </div>

                <div v-if="!mutableMaintenanceRequest.archived_at" class="flex flex-wrap gap-2 items-center mb-2 mx-2 grid grid-cols-1 lg:grid-cols-2">
                    <div class="col-span-2 lg:col-span-1 flex items-center">
                        <button
                            class="flex justify-center w-full whitespace-nowrap bg-indigo-600 hover:bg-indigo-700 px-4 py-2 border border-transparent text-sm font-medium shadow-sm rounded-l-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600 transition duration-300"
                            @click="openTicketCreateForm">
                            Create Ticket
                        </button>

                        <div class="dropdown w-10">
                            <button class="p-2 w-10 dropdown-toggle rounded-r-md border hover:border-gray-400 outline-none transition duration-300"
                                    id="dropDownTicketOption"
                                    data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            </button>
                            <div class="dropdown-menu dropdown-menu-right py-1" aria-labelledby="dropdownMenuButton">
                                <button class="dropdown-item py-2 transition duration-300"
                                        @click="addToExistingTicket">
                                    Add to Existing Ticket
                                </button>
                            </div>
                        </div>
                    </div>

                    <button
                        class="col-span-2 lg:col-span-1 flex justify-center w-full whitespace-nowrap bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-100 disabled:text-gray-300 disabled:border-gray-200 px-4 py-2 border border-transparent text-sm font-medium shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition duration-300"
                        @click.prevent="toggleRejectModal()"
                        :disabled="rejectionDisabled"
                    >Reject Issues
                    </button>

                </div>

                <h4 class="text-base font-medium text-gray-900 mx-2 mt-4 mb-2">Issues Reported</h4>

                <div class="flex flex-col gap-y-1 gap-x-2 items-start rounded-md mx-2 mb-1">
                    <label v-if="mutableMaintenanceRequest.issues && nonRejectedIssues.length > 1 && !mutableMaintenanceRequest.archived_at">
                        <checkbox class="" @input="selectAllIssuesForTicket(mutableMaintenanceRequest.issues)" :value="allIssuesSelected"
                                  label="Select All">
                        </checkbox>
                    </label>

                    <label class="w-full rounded-md p-2 border flex items-center space-x-2 select-none"
                           :class="issueClass(issue)"
                           v-for="issue in mutableMaintenanceRequest.issues">
                        <!-- todo replace with a new checkbox component that can handle array v-models-->
                        <input v-if="!mutableMaintenanceRequest.archived_at" type="checkbox" :disabled="issue.rejected_at" :class="issue.rejected_at ?
                        'bg-red-200 border-red-300 cursor-not-allowed' :
                        'cursor-pointer hover:border-indigo-600 border-gray-400'"
                               class="focus:ring-indigo-600 h-4 w-4 mr-3 text-indigo-600 rounded transition duration-300"
                               :value="issue" :key="issue.id" v-model="selectedIssues">

                        <div class="flex flex-col mb-1 w-full">
                            <div class="flex flex-wrap gap-2 items-center justify-between w-full relative">
                                <div class="w-5/6">
                                    <div class="text-gray-800 font-medium text-sm flex flex-wrap items-center justify-between gap-1">
                                        <div class="flex items-center gap-2">
                                            <span>{{ issue.location }} {{ issue.item }}</span>
                                            <icon-check class="w-5 h-5 text-green-400 hover:text-green-300 absolute top-2 right-2" label="Linked to a ticket"
                                                        v-if="issue.used_for_ticket"></icon-check>
                                        </div>
                                    </div>
                                    <div class="text-gray-500 text-sm">{{ issue.description }}</div>
                                </div>

                                <div v-if="issue.media && issue.media.length > 0" class="w-full flex flex-wrap gap-1">
                                    <img v-for="media in issue.media" class="h-20 w-20 rounded cursor-pointer" :src="media.url"
                                         @click.prevent="viewFile(media)"/>
                                </div>

                                <div v-if="issue.resulting_ticket_issues.length > 0" class="w-full flex gap-2 cursor-pointer">
                                    <div v-for="resultingTicket in issue.resulting_ticket_issues" @click.prevent="goToTicket(resultingTicket.ticket_id)">
                                        <div class="inline flex items-center gap-2 font-normal rounded-md py-0.5 px-2 text-xs"
                                             :class="getTicketReferenceColorStyles(resultingTicket.ticket_status, resultingTicket.ticket_priority)">

                                            <span class="font-medium">
                                                {{ resultingTicket.ticket_reference_number }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="issue.rejected_at" class="w-full flex-items-center">
                                <badge
                                    class="text-red-600 text-xs bg-red-100 hover:bg-red-200 mt-2 py-0.5 flex items-center flex-shrink gap-2 cursor-default rounded-md">
                                    Rejected
                                    <button class="cursor-click hover:text-red-800 transition duration-300" @click="unrejectIssue(issue)">
                                        <icon-x-circle class="w-4 h-4 ml-2"></icon-x-circle>
                                    </button>
                                </badge>
                            </div>

                            <div>
                                <badge class="text-yellow-600 bg-yellow-100 mb-1 py-1 text-sm gap-2 rounded-md my-2"
                                       v-if="issue.used_for_ticket && selectedIssues.findIndex(i=> issue.id === i.id) > -1">
                                    Note: Issue already linked to a ticket
                                </badge>
                            </div>
                        </div>
                    </label>
                </div>

                <div class="p-2 flex items-center gap-2" v-if="mutableMaintenanceRequest.tickets_without_link_to_issues &&
                mutableMaintenanceRequest.tickets_without_link_to_issues.length >
                 0">
                    <h3 class="text-base text-gray-900 font-medium whitespace-nowrap">Other Tickets</h3>
                    <div class="flex flex-wrap items-center justify-start gap-1">
                        <template v-for="ticket in mutableMaintenanceRequest.tickets_without_link_to_issues">
                            <div class="flex items-center px-2 py-0.5 rounded-md text-xs font-medium cursor-pointer"
                                 :class="getTicketReferenceColorStyles(ticket.status,ticket.priority)"
                                 @click.prevent="goToTicket(ticket.id)"
                            >{{ ticket.reference }}
                            </div>
                        </template>
                    </div>

                </div>

                <div class="flex flex-wrap mt-4 justify-between items-center">
                    <div>
                        <a target="_blank"
                           class="text-gray-600 font-normal m-1 p-1 active:text-indigo-600 hover:text-indigo-600 hover:text-indigo-600 transition duration-300"
                           :href="`/activity-log?id=${activityLogIdAndType}`">
                            View Activity Log
                        </a>

                        <button class="text-gray-600 font-normal m-1 p-1 active:text-yellow-600 hover:text-yellow-600 hover:text-yellow-600 transition duration-300"
                                @click.prevent="archiveOrUnarchiveModel(!mutableMaintenanceRequest.archived_at)">
                            {{ mutableMaintenanceRequest.archived_at ? "Unarchive" : "Archive" }}
                        </button>
                    </div>


                    <button v-if="!mutableMaintenanceRequest.archived_at"
                            class="flex justify-center whitespace-nowrap bg-white text-gray-600 border-gray-200 hover:border-gray-300 m-2 px-4 py-2 border border-transparent text-sm font-medium shadow-sm rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:border-gray-300 transition duration-300"
                            @click="toggleRequestStatus"
                            :disabled="statusChangeForm.busy">
                        {{ mutableMaintenanceRequest.closed_at ? "Reopen Request" : "Close Request" }}
                    </button>
                </div>
            </div>

            <div class="w-100 md:min-w-2/5 py-4 self-start">
                <div class="bg-white h-full rounded-md shadow p-2">
                    <div class="flex flex-col my-1 px-2 min-h-full">
                        <div class="flex justify-between items-center">
                            <h4 class="text-sm font-medium text-gray-500 mb-1">Administrator Notes</h4>
                            <span :class="notesCharCount < 0 ? 'text-red-600' : 'text-gray-400'"> {{ notesCharCount }} </span>
                        </div>

                        <textarea
                            :value="notesForm.notes"
                            @input="input=> notesForm.notes = input.target.value"
                            maxlength="500"
                            class="form-control text-sm flex mb-2 p-2 rounded p-2 text-gray-500 border border-gray-200 hover:border-gray-400 cursor-pointer"
                            :placeholder="[mutableMaintenanceRequest.archived_at ? 'Not Available' : 'No notes yet. Click here to add some...']"
                            @keydown.enter="saveNotes()"
                            :readonly="mutableMaintenanceRequest.archived_at"
                            rows="5"/>
                        <div class="text-red-500 text-xs break-normal mt-1" v-show="notesForm.errors.has('notes')">
                            {{ notesForm.errors.get("notes") }}
                        </div>

                        <secondary-button v-if="notesForm.notes != mutableMaintenanceRequest.notes" class="flex justify-center w-full"
                                          @click="saveNotes()">
                            Save Notes
                        </secondary-button>
                    </div>
                </div>
            </div>
        </div>

        <!--Add maintenance request to Existing Ticket-->
        <div v-if="showAddToExistingTicketForm" @close="dismissAddToExistingTicketForm">
            <div class="fixed z-1000 inset-0 overflow-y-hidden">
                <div class="flex min-h-auto align-top justify-center  pt-4 mt-10 px-4 pb-20 sm:p-0">
                    <div class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-black opacity-50"></div>
                    </div>

                    <!-- This element is to trick the browser into centering the modal contents. -->
                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen md:h-auto"></span>&#8203;

                    <div class="h-auto bg-white rounded text-left shadow-xl transform transition-all sm:my-8 sm:align-middle md:align-top"
                         role="dialog" aria-modal="true" aria-labelledby="modal-headline" style="width: 700px;">

                        <!--header-->
                        <div class="flex justify-between items-center bg-gray-100 rounded-t-lg w-100%">
                            <h2 class="px-5 text-md">Add to Existing Ticket</h2>
                            <button class="mr-4 font-thin text-lg text-gray-500 hover:text-blue-500" @click="dismissAddToExistingTicketForm">x</button>
                        </div>

                        <!--content/body-->
                        <div class="w-100% px-5 pt-1 pb-3">
                            <div class="flex flex-col">
                                <!--ticket dropdown search-->
                                <label class="flex-1 py-2">Ticket <span class="font-bold" v-if="selectedTicket">
                                    {{ selectedTicket.ticket_reference_number }}
                                </span>
                                </label>

                                <remote-select
                                    class="flex-1"
                                    v-model="selectedTicket"
                                    placeholder="Start typing to search Tickets or Properties"
                                    item-label="asset_name"
                                    :loading="loading"
                                    :data="tickets"
                                    :remote-method="searchOpenTickets"
                                    @focus="searchOpenTickets">

                                    <template slot-scope="props">
                                        <div class="flex flex-col py-3 leading-snug">
                                            <div class="flex flex-col md:flex-row md:justify-between md:items-center md:align-center -my-2 pb-2">
                                                <div>
                                                    <span class="font-bold text-black text-base pr-1">{{ props.item.ticket_reference_number }}</span>
                                                    <span class="text-gray-400 text-sm">{{ props.item.logged_at | date }}</span>
                                                </div>
                                                <div class="flex">
                                                    <span class="text-indigo-700 overflow-hidden truncate md:ml-2">{{ props.item.asset_name }}</span>
                                                </div>
                                            </div>
                                            <div class="flex flex-col-reverse md:flex-row md:justify-between md:align-center">
                                                <span
                                                    class="text-gray-400 overflow-hidden truncate text-sm">{{ props.item.description ? props.item.description : "No ticket description available" }}</span>
                                                <span class="text-gray-700 overflow-hidden truncate md:ml-2">
                                                    {{ props.item.service_provider_name ? props.item.service_provider_name : "" }}
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                </remote-select>

                                <div class="text-red-500 text-xs break-normal mt-1" v-show="maintenanceRequestForm.errors.has('ticket_id')">
                                    {{ maintenanceRequestForm.errors.get("ticket_id") }}
                                </div>

                                <!--Issues List-->
                                <label class="flex-1 py-2">
                                    {{ issueMessage }}
                                </label>
                                <div v-if="availableIssues" v-for="issue in availableIssues">
                                    <label class="custom-control custom-checkbox pb-1 flex" style="cursor: pointer">
                                        <!--simply giving each checkbox in a for loop the same modal, will they populate the array with the chosen :value-->
                                        <input type="checkbox" class="custom-control-input" :value="issue" :id="issue.id"
                                               v-model="selectedIssuesForExistingTicket">
                                        <span class="custom-control-label font-bold">{{ issue.item }}</span>
                                        <span v-if="issue.description" class="overflow-hidden truncate ml-1"> - {{ issue.description }}</span>
                                    </label>
                                </div>

                                <!--Show Close by default and Link when there is no existing link, or an existing link but new selected issues-->
                                <div class="flex justify-end"
                                     v-if="availableIssues.length > 0 && selectedTicket &&
                                         (!mutableMaintenanceRequest.tickets.some(e => e.id === selectedTicket.id) || selectedIssuesForExistingTicket.length > 0)">
                                    <button @click="linkToExistingTicket" class="button button-primary md:w-1/6 active:border-none">Link</button>
                                </div>
                                <div v-else class="flex justify-end">
                                    <button @click="dismissAddToExistingTicketForm" class="button button-primary md:w-1/6 active:border-none">Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ticket-create-modal-base
            :show="showTicketCreateForm"
            :maintenance-request="mutableMaintenanceRequest"
            :selected-issues="selectedIssues"
            @created-from-maintenance-request="updateMaintenanceRequestOnTicketCreation"
            @close-modal="dismissTicketCreateForm">
        </ticket-create-modal-base>

        <create-new-sms
            class="pt-40"
            v-if="mutableMaintenanceRequest"
            ref="smsCreateForm"
            contactable="App\MaintenanceRequest"
            :contactable-id="mutableMaintenanceRequest.id"
            category="maintenance requests">
        </create-new-sms>

        <simple-modal :show="!!(emailThread && emailThread.id)" max-width="sm:max-w-3xl" @close="emailThread = {}"
                      :title="!!(emailThread && emailThread.draft_email) ? '' : 'Viewing Email'" title-class="font-normal"
                      :body-class="!!(emailThread && emailThread.draft_email) ? '' : 'px-2 py-4'"
                      :show-title="!(emailThread && emailThread.draft_email)">
            <view-thread :thread="emailThread"
                         :context="emailContext"
                         v-if="userHasMailbox"
                         @close="emailThread = {}"
                         @forward-thread="threadForwardCreated"
                         @clear-context="clearEmailContext">
            </view-thread>
        </simple-modal>
        <template v-if="hasFeature('feat_image_viewer')">
            <image-viewer-modal :images="lightBoxImageUrls" v-if="showImageViewer" :show="showImageViewer" @close="toggleImageViewer()"
                                :selected-index="selectedImageIndex"></image-viewer-modal>
        </template>
        <template v-else>
            <lightbox :images="lightBoxImageUrls" :width="500" :height="500"></lightbox>
        </template>


        <reject-maintenance-request-issues-modal :show="showRejectionModal" v-if="showRejectionModal" :tenant="mutableMaintenanceRequest.contact"
                                                 :maintenance-request-id="mutableMaintenanceRequest.id" :selected-issue-ids="selectedIssues.map(i => i.id)"
                                                 @reject-issues="setIssuesRejected" @toggle-modal="toggleRejectModal">
        </reject-maintenance-request-issues-modal>

        <archive-and-unarchive-modal :show="showArchivingModal" :model-id="mutableMaintenanceRequest.id" model-type="maintenance request"
                                     url-override="maintenance/requests" :archiving="isArchiving"
                                     @actioned="afterArchiving" v-if="showArchivingModal" @close="toggleArchivingModal">
        </archive-and-unarchive-modal>
    </div>
</template>

<script>
import ArchivingMixin from "../../mixins/archiving-mixin";
import ComposeMail from "../../mixins/compose-mail";
import ticketSearch from "../../mixins/ticket-search";
import assetSearch from "../../mixins/asset-search";
import maintenanceRequestMixin from "../../mixins/maintenance-request-mixin";

export default {
    name: "MaintenanceRequestsDetailPanel",

    mixins: [
        ArchivingMixin,
        ComposeMail,
        ticketSearch,
        assetSearch,
        maintenanceRequestMixin
    ],

    props: {
        maintenanceRequest: {
            required: true,
            description: "The maintenance request we are showing details of"
        },
    },

    data () {
        return {
            mutableMaintenanceRequest: this.maintenanceRequest,
            loading: true,
            assetLinkForm: new AppForm({
                asset_id: null
            }),
            selectedAsset: null,
            assetLinkShow: false,

            showTicketCreateForm: false,
            selectedTicket: null,

            showAddToExistingTicketForm: false,
            selectedIssuesForExistingTicket: [],

            selectedIssues: [],
            availableIssues: [],
            issueMessage: "Select a ticket to see the reported issues not associated with that ticket yet",
            maintenanceRequestForm: new AppForm({
                maintenance_request_id: null,
                issues: null,
                ticket_id: null,
                required_action: "No Action"
            }),

            notesForm: new AppForm({
                notes: ""
            }),
            statusChangeForm: new AppForm({}),

            emailContext: {},

            showRejectionModal: false,
            showImageViewer: false,
            selectedImageIndex: null,
        };
    },

    computed: {
        lightBoxImageUrls () {
            if (!this.mutableMaintenanceRequest) {
                return [];
            }

            let fullImageList = [];

            this.mutableMaintenanceRequest.issues.forEach(issue => {
                if (issue.media.length > 0) {
                    issue.media.forEach(photo => {
                        fullImageList.push(photo.url);
                    });
                }
            });

            return fullImageList;
        },

        userHasLinkMaintenanceRequestsPermission () {
            return this.app.state.permissions.includes("link-maintenance-requests");
        },

        allIssuesSelected () {
            return this.nonRejectedIssues.length > 0 && this.selectedIssues.length === this.nonRejectedIssues.length;
        },

        rejectionDisabled () {
            return !(this.selectedIssues && this.selectedIssues.length > 0);
        },

        nonRejectedIssues () {
            return this.mutableMaintenanceRequest.issues.filter(issue => !issue.rejected_at);
        },

        activityLogIdAndType () {
            return this.mutableMaintenanceRequest.asset && this.mutableMaintenanceRequest.asset.id ? this.mutableMaintenanceRequest.asset.id + "&type=Asset" :
                this.mutableMaintenanceRequest.id + "&type=MaintenanceRequest";
        },

        notesCharCount () {
            return 500 - this.notesForm.notes.length;
        },
    },

    watch: {
        maintenanceRequest (newRequest) {
            this.$nextTick(function () {
                this.assetLinkShow = false;
                this.mutableMaintenanceRequest = newRequest;
                this.resetComponent();
            });
        },

        selectedTicket: {
            handler () {
                this.availableIssues = [];
                if (this.selectedTicket !== null) {
                    this.populateAvailableIssues();
                } else {
                    this.issueMessage = "Select a ticket to see the reported issues not associated with that ticket yet";
                }
            },
        },
    },

    mounted () {
        this.resetComponent();
    },

    methods: {
        resetComponent () {
            this.selectedAsset = this.mutableMaintenanceRequest.asset;
            this.emailCreateUrl = "/maintenance/requests/" + this.mutableMaintenanceRequest.id + "/emails/create";
            this.setNotesFormValues();
            this.updateMaintenanceRequestForm();
        },

        linkOrUnlinkMaintenanceRequestToAsset (maintenanceRequest, selectedAsset = null) {
            // Don't link the same asset
            if (maintenanceRequest?.asset?.id === selectedAsset?.id) {
                return;
            }

            this.assetLinkForm.asset_id = selectedAsset ? selectedAsset.id : null;
            App.post("/maintenance/requests/" + maintenanceRequest.id + "/link-to-asset", this.assetLinkForm)
            .then((response) => {
                maintenanceRequest.asset = response;
                this.assetLinkForm = new AppForm({});
                this.selectedAsset = null;
                toastr.success(selectedAsset ? "Successfully linked asset" : "Successfully unlinked asset");
            })
            .catch(errors => toastr.error(errors.message));
        },

        openAssetLink () {
            this.assetLinkShow = true;
            this.$nextTick(() => {
                this.$refs.selectAsset.focus();
            });
        },

        closeAssetLink () {
            this.linkOrUnlinkMaintenanceRequestToAsset(this.mutableMaintenanceRequest, this.selectedAsset);
            this.$refs.selectAsset.clearInput();
            this.assetLinkShow = false;
        },

        addToExistingTicket () {
            this.searchOpenTickets();
            this.showAddToExistingTicketForm = true;
            this.assetLinkShow = false;
        },

        searchOpenTickets (query) {
            this.maintenanceRequestForm.errors.forget("ticket_id");
            this.searchTickets(query);
        },

        selectAllIssuesForTicket (issues) {
            if (this.allIssuesSelected) {
                this.selectedIssues.splice(0);
            } else {
                issues.forEach(issue => {
                    if (!this.selectedIssues.includes(issue) && !issue.rejected_at) {
                        this.selectedIssues.push(issue);
                    }
                });
            }
        },

        linkToExistingTicket () {
            //check if ticket is selected, manually set error message for form
            if (this.selectedTicket === null) {
                return this.maintenanceRequestForm.setErrors({ticket_id: ["Please select a ticket to create a link"]});
            }

            //form to send for the patch request
            this.updateMaintenanceRequestForm();
            //take ticket id, issuesList and make link to maintenance request on ticket, then create issues.
            App.patch(`/maintenance/ticket/requests/${this.selectedTicket.id}/update`, this.maintenanceRequestForm)
            .then(response => {
                this.mutableMaintenanceRequest = response;
                this.selectedIssuesForExistingTicket.splice(0);
                this.selectedIssues.splice(0);
                window.toastr.success("Ticket Details Updated!");
            }).catch((error) => {
                toastr.error(error.message);
            });

            //close and reset variables
            this.dismissAddToExistingTicketForm();
            this.$emit("fetch-requests");
        },

        populateAvailableIssues () {
            this.loading = true;
            this.selectedIssuesForExistingTicket = [];

            axios.get(`/maintenance/ticket/requests/issues`, {
                params: {"ticket_id": this.selectedTicket.id, "selected_request_id": this.mutableMaintenanceRequest.id}
            })
            .then(response => {
                this.availableIssues = response.data;

                // set message in response otherwise it does not follow the correct logic
                if (this.availableIssues.length === 0) {
                    this.issueMessage = "All issues from this maintenance request has already been added to the selected ticket";

                    if (this.mutableMaintenanceRequest.issues.length === 0) {
                        this.issueMessage = "There are no reported issues on this maintenance request";
                    }
                } else {
                    this.issueMessage = "Optionally add issues to ticket";
                }

                // Tick already selected issues
                this.availableIssues.filter(issue => this.selectedIssues.findIndex(i => i.id === issue.id) > -1)
                .forEach(issue => this.selectedIssuesForExistingTicket.push(issue));

                this.loading = false;
            })
            .catch(error => {
                this.loading = false;
                toastr.error(error.message);
            });
        },

        dismissAddToExistingTicketForm () {
            this.showAddToExistingTicketForm = false;
            this.selectedIssuesForExistingTicket = [];
            this.selectedTicket = null;
        },

        updateMaintenanceRequestForm () {
            this.maintenanceRequestForm["maintenance_request_id"] = this.mutableMaintenanceRequest.id;
            this.maintenanceRequestForm["ticket_id"] = this.selectedTicket ? this.selectedTicket.id : null;
            this.maintenanceRequestForm["issues"] = this.selectedIssuesForExistingTicket ? this.selectedIssuesForExistingTicket : null;
            //give each issue a required action of No Action
            if (this.selectedIssuesForExistingTicket) {
                for (let i = 0; i < this.maintenanceRequestForm.issues.length; i++) {
                    this.maintenanceRequestForm.issues[i].required_action = "No Action";
                }
            }
        },

        setNotesFormValues () {
            this.notesForm.notes = this.mutableMaintenanceRequest.notes ?? "";
        },

        saveNotes () {
            if (this.notesForm.notes !== this.mutableMaintenanceRequest.notes) {
                App.patch("/maintenance/requests/" + this.mutableMaintenanceRequest.id + "/notes/update", this.notesForm)
                .then(response => {
                    this.mutableMaintenanceRequest = response;
                    this.setNotesFormValues();
                    this.$emit("update-request", this.mutableMaintenanceRequest);
                    toastr.success("Notes updated!");
                })
                .catch(error => {
                    toastr.error(error.message);
                });
            }
        },

        toggleRequestStatus () {
            this.statusChangeForm.status = this.mutableMaintenanceRequest.status === "new" ? "closed" : "new";

            App.patch("/maintenance/requests/" + this.mutableMaintenanceRequest.id + "/status/update", this.statusChangeForm)
            .then(response => {
                this.mutableMaintenanceRequest.status = this.statusChangeForm.status;
                this.mutableMaintenanceRequest.closed_at = this.mutableMaintenanceRequest.status === "new" ? null : moment();
                toastr.success(response);
            })
            .catch(error => {
                toastr.error(error.message);
            });
        },

        openTicketCreateForm () {
            this.showTicketCreateForm = true;
            this.assetLinkShow = false;
        },

        dismissTicketCreateForm () {
            this.$emit("fetch-requests");
            this.showTicketCreateForm = false;
        },

        updateMaintenanceRequestOnTicketCreation (ticket) {
            if (this.allIssuesSelected) {
                this.mutableMaintenanceRequest.closed_at = ticket.created_at;
                this.mutableMaintenanceRequest.closed_by = {id: this.state.user.id, name: this.state.user.name};
            }

            this.mutableMaintenanceRequest.issues.forEach(requestIssue => {
                if (this.selectedIssues.includes(requestIssue)) {
                    requestIssue.used_for_ticket = 1;
                    requestIssue.resulting_ticket_issues.push({
                        ticket_id: ticket.id,
                        ticket_status: ticket.status,
                        ticket_priority: this.$options.filters.titlecase(ticket.priority),
                        ticket_reference_number: ticket.ticket_reference_number,
                        maintenance_request_issue_id: requestIssue.id
                    });
                }

            });

            if (!this.selectedIssues || this.selectedIssues.length <= 0) {
                this.mutableMaintenanceRequest.tickets_without_link_to_issues.push({
                    id: ticket.id,
                    status: ticket.status,
                    priority: this.$options.filters.titlecase(ticket.priority),
                    reference: ticket.ticket_reference_number
                });
            }

            this.selectedIssues.splice(0);
            this.$emit("update-request", this.mutableMaintenanceRequest);
        },

        createEmailForContact () {
            this.createEmail(this.mutableMaintenanceRequest.contact.id, null, null);
        },

        showSmsForm () {
            this.$refs.smsCreateForm.showSMSCreateForm();
        },

        viewFile (media) {
            Bus.$emit("view-photo", media.url);
            this.selectedImageIndex = _.flattenDeep(this.maintenanceRequest.issues.map(issue => issue.media)).indexOf(media);
            this.toggleImageViewer();
        },

        issueClass (issue) {
            let issueClass = "border-gray-200 hover:border-gray-100 cursor-pointer";

            if (issue.used_for_ticket) {
                issueClass = "border-green-400 hover:border-green-300 cursor-pointer";
            }

            if (issue.rejected_at) {
                issueClass = "border-red-200 cursor-not-allowed";
            }

            return issueClass;
        },

        unrejectIssue (issue) {
            App.post(`/maintenance/requests/issues/${issue.id}/unreject`, new AppForm({}))
            .then(response => {
                this.$set(issue, "rejected_at", null);
                toastr.success(response);
            })
            .catch(error => {
                toastr.error(error.message);
            });
        },

        toggleRejectModal () {
            if (!this.showRejectionModal && !_.isEmpty(this.selectedIssues.filter(issue => issue.used_for_ticket))) {
                toastr.error("One or more issues have already been linked to a ticket");
                return;
            }

            this.showRejectionModal = !this.showRejectionModal;
        },

        setIssuesRejected () {
            let dateTimeNow = this.$dayjs().format("YYYY-MM-DD");
            this.selectedIssues.map(i => i.rejected_at = dateTimeNow);
            this.selectedIssues.splice(0);

            // Because rejection happens in a job we need to update mutableMaintenanceRequest to be closed if all issues are rejected or used for ticket.
            if (this.mutableMaintenanceRequest.issues.filter(issue => issue.rejected_at != null || issue.used_for_ticket).length === this.mutableMaintenanceRequest.issues.length) {
                this.$set(this.mutableMaintenanceRequest, "status", "closed");
                this.$set(this.mutableMaintenanceRequest, "closed_at", dateTimeNow);
                this.$set(this.mutableMaintenanceRequest, "closed_by", {id: this.state.user.id, name: this.state.user.name});
                this.$emit("fetch-requests");
            }
        },

        afterArchiving (archiving) {
            if (archiving) {
                this.mutableMaintenanceRequest.archived_at = this.$dayjs().format();
            } else {
                this.mutableMaintenanceRequest.archived_at = null;
            }

            this.$emit("update-request", this.mutableMaintenanceRequest, true);
        },

        handleAssetLinkingClickOutside () {
            this.assetLinkShow = false;
        },

        showActivityLogForRequest () {
            window.open(`/activity-log?id=${this.activityLogIdAndType}`, "_blank");
        },

        toggleImageViewer () {
            this.showImageViewer = !this.showImageViewer;

            if (!this.showImageViewer) {
                this.selectedImageIndex = null;
            }
        },
    },
};
</script>

<style scoped>
.stick-below-nav {
    position: sticky !important;
    top: 78px !important;
}
</style>
